import './settings/settings-leaf.css';
import { useEffect, useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { authApi } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { useSession } from '../state/session.tsx';
import { Alert, Button, TextInput } from './ui.tsx';

/**
 * The details a signed-in person owns about themselves.
 *
 * Deliberately separate from the team list further up the page, which is
 * administration and needs `users:manage`: everyone can edit their own name and
 * number, and an estimator who cannot see the team list still needs somewhere
 * to put their mobile so "text this to me" has a default.
 *
 * The phone is unverified and authorizes nothing. It is said plainly in the
 * hint, because a phone field on an account page reads like two-factor setup
 * unless it says otherwise.
 */
export default function ProfileSection() {
  const { t } = useTranslation();
  const { user, refresh } = useSession();

  const [fullName, setFullName] = useState(() => user?.fullName ?? '');
  const [phone, setPhone] = useState(() => user?.phone ?? '');
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // The session query owns the record; mirror it once it lands, the same way
  // the company form above does.
  useEffect(() => {
    setFullName(user?.fullName ?? '');
    setPhone(user?.phone ?? '');
  }, [user?.fullName, user?.phone]);

  const save = useMutation({
    mutationFn: () => authApi.updateProfile({ fullName: fullName.trim(), phone: phone.trim() }),
    onSuccess: async () => {
      setFieldErrors({});
      setMessage({ tone: 'success', text: t('profile.saved') });
      // Every screen that reads the signed-in person — the header, the test
      // message box in the SMS section — reads them off the session.
      await refresh();
    },
    onError: (caught) => {
      if (caught instanceof ApiRequestError) {
        setFieldErrors(caught.fields);
        setMessage(Object.keys(caught.fields).length > 0 ? null : { tone: 'error', text: caught.message });
      } else {
        setMessage({ tone: 'error', text: t('profile.saveError') });
      }
    },
  });

  if (!user) return null;

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setMessage(null);
    save.mutate();
  }

  return (
    <section className="card work-section settings-leaf mb-6" aria-labelledby="profile-heading">
      <div className="work-section-heading">
        <h2 id="profile-heading" className="mb-3 text-lg font-semibold">
          {t('profile.heading')}
        </h2>
      </div>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('profile.description')}</p>

      {message ? (
        <div className="mb-4">
          <Alert tone={message.tone}>{message.text}</Alert>
        </div>
      ) : null}

      <form onSubmit={handleSubmit} noValidate>
        <div className="grid gap-x-4 sm:grid-cols-2">
          <TextInput
            label={t('profile.fullName')}
            value={fullName}
            error={fieldErrors.fullName}
            onChange={(event) => setFullName(event.target.value)}
          />
          <TextInput
            label={t('profile.phone')}
            type="tel"
            autoComplete="tel"
            hint={t('profile.phoneHint')}
            value={phone}
            error={fieldErrors.phone}
            onChange={(event) => setPhone(event.target.value)}
          />
        </div>
        <div className="mt-4">
          <Button type="submit" variant="primary" busy={save.isPending}>
            {t('profile.save')}
          </Button>
        </div>
      </form>
    </section>
  );
}
